import { Box, Button, CircularProgress, FormControl, List, ListItem, TextField, Theme, Typography, styled } from "@mui/material";
import { ValidationHintText } from "components/shared";
import { IAttachmentFormPayload, IAttachmentValidator } from "models/attachments";
import { ChangeEvent, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { fieldHasErrors } from "utils/validation-utils";

type AttachmentFileFormProps = {
    payload: IAttachmentFormPayload;
    updatePayload: React.Dispatch<Partial<IAttachmentFormPayload>>;
    validator: IAttachmentValidator;
};

//-------------------- STYLED COMPONENTS --------------------
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});  

const FileTypeMessage = 'Automatically generated from uploaded file';

const AttachmentFileForm: React.FC<AttachmentFileFormProps> = (props) => {
    const {
        payload,
        updatePayload,
        validator
    } = props;

    const onDrop = (acceptedFiles: File[]) => {
        applyFiles(acceptedFiles);
    };

    const isTypeSelected = payload.typeId != 0;
    const isNameWorking = validator.state.mode === 'name' && validator.state.working;
    const isFileWorking = validator.state.mode === 'file' && validator.state.working;

    const {
        getRootProps,
        getInputProps
    } = useDropzone({
        onDrop,
        noClick: true,
        disabled: !isTypeSelected || isFileWorking
    })

    //-------------------- REF VARIABLES --------------------
    const uploadRef = useRef<HTMLInputElement>(null)  
     
    //-------------------- HANDLERS --------------------    
    const applyFiles = async (files: File[]) => {
        if (files.length === 0) 
            return;

        const filenames = [];

        for (var file of files) {
            filenames.push(file.name);
        }

        if (await validator.validate('file', { filenames })) {
            updatePayload({
                files: files,
                name: filenames.join(',')
            });
        } else {
            updatePayload({
                files: null,
                name: ''
            });
        }
    };
    const handleUpload = async(e: ChangeEvent<HTMLInputElement>) => {        
        if (e.target.files === null || e.target.files.length === 0) {            
            return;
        }
        
        const value = e.target.files;
        const files: File[] = [];
        
        for (var file of value) {
            files.push(file);
        }

        await applyFiles(files);
    };

    const invalidFileName = fieldHasErrors(validator.summary, 'name');
    const invalidFile = fieldHasErrors(validator.summary, 'file');
    
    const colorSelect = (theme: Theme) => {
        if (invalidFile) return theme.palette.error.main;
        if (payload.files) {
            return theme.palette.text.primary;
        } else {
            return theme.palette.text.disabled;
        }
    };

    return (
        <List>
            <ListItem>
                <FormControl fullWidth>
                <TextField
                    id="fileName"
                    name="fileName"
                    label="Attachment Name:"
                    placeholder={FileTypeMessage}
                    hiddenLabel
                    fullWidth
                    value={(payload.name?.length ?? 0) > 0 ? payload.name : FileTypeMessage}
                    disabled={true}
                    error={invalidFileName}
                    helperText={
                        <ValidationHintText name='name'
                            validationSummary={validator.summary}
                        />
                    }
                    required={true}
                    InputProps={{
                        endAdornment: isNameWorking ? <CircularProgress 
                            size={20}
                            sx={{                                
                                position: 'unset'
                            }}
                        /> : null
                    }}
                />
                </FormControl>
            </ListItem>
            <ListItem>
                <Box sx={{
                    padding: 1,
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    width: '100%',
                    borderColor: (theme) => theme.palette.grey[100]
                }} {...getRootProps()}>
                    <Button component="label" variant="outlined" 
                        sx={{
                            marginLeft: 0
                        }}
                        disabled={!isTypeSelected || isFileWorking} 
                        endIcon={isFileWorking ? <CircularProgress 
                            size={20}
                            sx={{                                
                                position: 'unset',
                                color: (theme) => theme.palette.primary.contrastText
                            }}
                            
                        /> : null}
                    >
                        Choose file
                        <VisuallyHiddenInput type="file" ref={uploadRef}
                            onChange={handleUpload}
                            disabled={!isTypeSelected || isFileWorking} 
                            multiple
                            style={{ display: 'none' }} 
                            {...getInputProps()}/>
                    </Button>
                    <Typography variant='caption' fontWeight='bold'
                        // color={colorSelect}
                    >
                        { payload.files ? payload.name 
                            : <ValidationHintText name='file' defaultContent='No File Chosen'
                                validationSummary={validator.summary}
                            />}
                    </Typography>
                    
                </Box>
            </ListItem>
        </List>
    );
};

export default AttachmentFileForm;